import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Trophy, Radio, Clock, Bell, BellOff, Send,
    Square, AlertCircle, Check, ChevronDown, RefreshCw
} from 'lucide-react';
import Card from '../../components/Card';
import Table from '../../components/Table';
import Select from '../../components/Select';
import Badge from '../../components/Badge';
import Modal from '../../components/Modal';
import Button from '../../components/Button';
import Input from '../../components/Input';
import Textarea from '../../components/Textarea';
import {
    getBasketballCompetitions,
    getBasketballGames,
    getBasketballGameDetails,
    sendCustomGamePush,
    triggerStandardGamePush,
    forceStopGame
} from '../../utils/api';
import toast from 'react-hot-toast';

// Notification type labels
const NOTIFICATION_LABELS = {
    game_start: 'Game Start',
    quarter_1_end: 'Q1 End',
    quarter_2_end: 'Q2 End',
    quarter_3_end: 'Q3 End',
    regulation_end: 'Regulation End',
    overtime_end: 'Overtime End',
    game_end: 'Game End'
};

// Game status badge
function GameStatusBadge({ status, streamStatus, liveData }) {
    if (status === 'LIVE') {
        const quarter = liveData?.periodCurrent || '?';
        const clock = liveData?.clock || '';
        return (
            <div className="flex items-center gap-2">
                <Badge variant="danger" className="animate-pulse">
                    <Radio size={12} className="mr-1" /> LIVE
                </Badge>
                <span className="text-sm text-muted-light dark:text-muted-dark">
                    Q{quarter} {clock}
                </span>
            </div>
        );
    }

    const variants = {
        SCHEDULED: 'info',
        COMPLETE: 'success',
        POSTPONED: 'warning',
        CANCELLED: 'danger'
    };

    return <Badge variant={variants[status] || 'default'}>{status}</Badge>;
}

// Notification status icons
function NotificationStatusIcons({ notificationStatus, customNotificationsCount }) {
    const icons = [
        { key: 'game_start', label: 'Start', short: 'S' },
        { key: 'quarter_1_end', label: 'Q1', short: '1' },
        { key: 'quarter_2_end', label: 'Q2', short: '2' },
        { key: 'quarter_3_end', label: 'Q3', short: '3' },
        { key: 'game_end', label: 'End', short: 'E' }
    ];

    return (
        <div className="flex items-center gap-1">
            {icons.map(({ key, short }) => {
                const isSent = notificationStatus?.[key];
                return (
                    <div
                        key={key}
                        className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-semibold transition-colors ${isSent
                            ? 'bg-green-500 text-white ring-2 ring-green-300 dark:ring-green-600'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
                            }`}
                        title={`${NOTIFICATION_LABELS[key]}${isSent ? ' (Sent)' : ' (Not sent)'}`}
                    >
                        {short}
                    </div>
                );
            })}
            {customNotificationsCount > 0 && (
                <Badge variant="purple" className="ml-1">
                    +{customNotificationsCount}
                </Badge>
            )}
        </div>
    );
}

export default function GamesManagement() {
    const queryClient = useQueryClient();
    const [selectedCompetition, setSelectedCompetition] = useState(null);
    const [selectedGame, setSelectedGame] = useState(null);
    const [showGameModal, setShowGameModal] = useState(false);
    const [showCustomPushModal, setShowCustomPushModal] = useState(false);
    const [showTriggerModal, setShowTriggerModal] = useState(false);
    const [showForceStopModal, setShowForceStopModal] = useState(false);
    const [triggerType, setTriggerType] = useState(null);

    // Form states
    const [customTitle, setCustomTitle] = useState('');
    const [customBody, setCustomBody] = useState('');
    const [triggerHomeScore, setTriggerHomeScore] = useState('');
    const [triggerAwayScore, setTriggerAwayScore] = useState('');
    const [forceHomeScore, setForceHomeScore] = useState('');
    const [forceAwayScore, setForceAwayScore] = useState('');

    // Fetch competitions
    const { data: competitionsData, isLoading: competitionsLoading } = useQuery({
        queryKey: ['basketball-competitions'],
        queryFn: getBasketballCompetitions
    });

    // Set first competition as default
    useEffect(() => {
        if (competitionsData?.data?.length > 0 && !selectedCompetition) {
            setSelectedCompetition(competitionsData.data[0]._id);
        }
    }, [competitionsData, selectedCompetition]);

    // Fetch games
    const { data: gamesData, isLoading: gamesLoading, refetch: refetchGames } = useQuery({
        queryKey: ['basketball-games', selectedCompetition],
        queryFn: () => getBasketballGames(selectedCompetition),
        enabled: !!selectedCompetition,
        refetchInterval: 30000 // Refresh every 30 seconds
    });

    // Fetch game details
    const { data: gameDetails, isLoading: gameDetailsLoading } = useQuery({
        queryKey: ['basketball-game-details', selectedGame?._id],
        queryFn: () => getBasketballGameDetails(selectedGame._id),
        enabled: !!selectedGame?._id && showGameModal
    });

    // Send custom push mutation
    const sendCustomPushMutation = useMutation({
        mutationFn: ({ matchId, title, body }) => sendCustomGamePush(matchId, { title, body }),
        onSuccess: () => {
            toast.success('Custom notification sent');
            setShowCustomPushModal(false);
            setCustomTitle('');
            setCustomBody('');
            queryClient.invalidateQueries(['basketball-games']);
            queryClient.invalidateQueries(['basketball-game-details']);
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to send notification');
        }
    });

    // Trigger standard push mutation
    const triggerPushMutation = useMutation({
        mutationFn: ({ matchId, notificationType, homeScore, awayScore }) =>
            triggerStandardGamePush(matchId, { notificationType, homeScore, awayScore }),
        onSuccess: () => {
            toast.success('Notification triggered');
            setShowTriggerModal(false);
            setTriggerType(null);
            setTriggerHomeScore('');
            setTriggerAwayScore('');
            queryClient.invalidateQueries(['basketball-games']);
            queryClient.invalidateQueries(['basketball-game-details']);
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to trigger notification');
        }
    });

    // Force stop mutation
    const forceStopMutation = useMutation({
        mutationFn: ({ matchId, homeScore, awayScore }) =>
            forceStopGame(matchId, { homeScore, awayScore }),
        onSuccess: () => {
            toast.success('Game stopped and completed');
            setShowForceStopModal(false);
            setShowGameModal(false);
            setForceHomeScore('');
            setForceAwayScore('');
            queryClient.invalidateQueries(['basketball-games']);
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to stop game');
        }
    });

    const handleGameClick = useCallback((game) => {
        setSelectedGame(game);
        setShowGameModal(true);
    }, []);

    const handleSendCustomPush = () => {
        if (!customTitle.trim() || !customBody.trim()) {
            toast.error('Title and body are required');
            return;
        }
        sendCustomPushMutation.mutate({
            matchId: selectedGame._id,
            title: customTitle,
            body: customBody
        });
    };

    const handleTriggerPush = () => {
        const data = {
            matchId: selectedGame._id,
            notificationType: triggerType
        };

        // Add scores for certain notification types
        if (['quarter_1_end', 'quarter_2_end', 'quarter_3_end', 'game_end', 'overtime_end'].includes(triggerType)) {
            if (triggerHomeScore) data.homeScore = parseInt(triggerHomeScore);
            if (triggerAwayScore) data.awayScore = parseInt(triggerAwayScore);
        }

        triggerPushMutation.mutate(data);
    };

    const handleForceStop = () => {
        forceStopMutation.mutate({
            matchId: selectedGame._id,
            homeScore: forceHomeScore ? parseInt(forceHomeScore) : undefined,
            awayScore: forceAwayScore ? parseInt(forceAwayScore) : undefined
        });
    };

    const openTriggerModal = (type) => {
        setTriggerType(type);
        // Pre-fill scores if available
        if (selectedGame) {
            setTriggerHomeScore(selectedGame.homeScore?.toString() || '');
            setTriggerAwayScore(selectedGame.awayScore?.toString() || '');
        }
        setShowTriggerModal(true);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const columns = [
        {
            header: 'Date',
            accessor: 'matchTimeUTC',
            render: (game) => (
                <span className="text-sm">{formatDate(game.matchTimeUTC)}</span>
            )
        },
        {
            header: 'Game',
            accessor: 'teams',
            render: (game) => (
                <div className="flex items-center gap-2">
                    <span className="font-medium">{game.homeTeam?.name}</span>
                    <span className="text-muted-light dark:text-muted-dark">vs</span>
                    <span className="font-medium">{game.awayTeam?.name}</span>
                </div>
            )
        },
        {
            header: 'Score',
            accessor: 'score',
            render: (game) => (
                <span className="font-mono font-medium">
                    {game.homeScore ?? '-'} - {game.awayScore ?? '-'}
                </span>
            )
        },
        {
            header: 'Status',
            accessor: 'status',
            render: (game) => (
                <GameStatusBadge
                    status={game.status}
                    streamStatus={game.streamStatus}
                    liveData={game.liveData}
                />
            )
        },
        {
            header: 'Push Status',
            accessor: 'notificationStatus',
            render: (game) => (
                <NotificationStatusIcons
                    notificationStatus={game.notificationStatus}
                    customNotificationsCount={game.customNotificationsCount}
                />
            )
        }
    ];

    const game = gameDetails?.data;
    const canSendCustomPush = selectedGame?.status === 'LIVE' &&
        (selectedGame?.customNotificationsCount || 0) < 2;
    const isGameLiveOrComplete = ['LIVE', 'COMPLETE'].includes(selectedGame?.status);

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold mb-2">Games Management</h1>
                    <p className="text-muted-light dark:text-muted-dark">
                        Monitor games and manage push notifications
                    </p>
                </div>
                <button
                    onClick={() => refetchGames()}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <RefreshCw size={18} />
                    Refresh
                </button>
            </div>

            {/* Competition Selector */}
            <Card>
                <div className="flex items-center gap-4">
                    <Trophy size={20} className="text-primary" />
                    <Select
                        value={selectedCompetition || ''}
                        onChange={(e) => setSelectedCompetition(e.target.value)}
                        className="flex-1"
                        disabled={competitionsLoading}
                    >
                        <option value="">Select Competition</option>
                        {competitionsData?.data?.map(comp => (
                            <option key={comp._id} value={comp._id}>
                                {comp.name} ({comp.season})
                            </option>
                        ))}
                    </Select>
                </div>
            </Card>

            {/* Games Table */}
            <Card>
                <Table
                    columns={columns}
                    data={gamesData?.data || []}
                    loading={gamesLoading || !selectedCompetition}
                    onRowClick={handleGameClick}
                    emptyState={{
                        title: 'No games found',
                        description: selectedCompetition
                            ? 'No games in this competition'
                            : 'Select a competition to view games'
                    }}
                />
            </Card>

            {/* Game Detail Modal */}
            <Modal
                isOpen={showGameModal}
                onClose={() => setShowGameModal(false)}
                title={game ? `${game.homeTeam?.name} vs ${game.awayTeam?.name}` : 'Game Details'}
                size="large"
            >
                {gameDetailsLoading ? (
                    <div className="flex justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    </div>
                ) : game ? (
                    <div className="space-y-6">
                        {/* Game Info */}
                        <div className="flex items-center justify-between">
                            <div>
                                <GameStatusBadge
                                    status={game.status}
                                    streamStatus={game.streamStatus}
                                    liveData={game.liveData}
                                />
                            </div>
                            <div className="text-3xl font-bold font-mono">
                                {game.homeScore ?? 0} - {game.awayScore ?? 0}
                            </div>
                        </div>

                        {/* Notification Status Grid */}
                        <div>
                            <h3 className="font-medium mb-3">Notification Status</h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {Object.entries(NOTIFICATION_LABELS).map(([key, label]) => {
                                    const sent = game.notifications?.some(n => n.type === key);
                                    const notification = game.notifications?.find(n => n.type === key);

                                    return (
                                        <div
                                            key={key}
                                            className={`p-3 rounded-lg border ${sent
                                                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                                : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                                                }`}
                                        >
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm font-medium">{label}</span>
                                                {sent ? (
                                                    <Check size={16} className="text-green-500" />
                                                ) : (
                                                    <BellOff size={16} className="text-gray-400" />
                                                )}
                                            </div>
                                            {notification && (
                                                <div className="text-xs text-muted-light dark:text-muted-dark">
                                                    {notification.sentByAdmin && (
                                                        <span className="text-orange-500">Manual</span>
                                                    )}
                                                    {notification.homeScore !== undefined && (
                                                        <span> ({notification.homeScore}-{notification.awayScore})</span>
                                                    )}
                                                </div>
                                            )}
                                            {!sent && isGameLiveOrComplete && (
                                                <button
                                                    onClick={() => openTriggerModal(key)}
                                                    className="mt-2 text-xs text-blue-600 hover:text-blue-700"
                                                >
                                                    Trigger manually
                                                </button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Custom Notifications */}
                        <div>
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="font-medium">
                                    Custom Notifications ({game.customNotifications?.length || 0}/2)
                                </h3>
                                {canSendCustomPush && (
                                    <Button
                                        size="sm"
                                        onClick={() => setShowCustomPushModal(true)}
                                    >
                                        <Send size={14} className="mr-1" />
                                        Send Custom
                                    </Button>
                                )}
                            </div>

                            {game.customNotifications?.length > 0 ? (
                                <div className="space-y-2">
                                    {game.customNotifications.map((cn, idx) => (
                                        <div key={idx} className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                                            <div className="font-medium">{cn.title}</div>
                                            <div className="text-sm text-muted-light dark:text-muted-dark">{cn.body}</div>
                                            <div className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                                                Sent by {cn.sentBy} at {new Date(cn.sentAt).toLocaleString()}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-sm text-muted-light dark:text-muted-dark">
                                    No custom notifications sent
                                </p>
                            )}
                        </div>

                        {/* Force Stop Section */}
                        {game.status === 'LIVE' && (
                            <div className="pt-4 border-t border-border-light dark:border-border-dark">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h3 className="font-medium text-red-600">Force Stop Game</h3>
                                        <p className="text-sm text-muted-light dark:text-muted-dark">
                                            Stop stream and mark as complete
                                        </p>
                                    </div>
                                    <Button
                                        variant="danger"
                                        onClick={() => {
                                            setForceHomeScore(game.homeScore?.toString() || '');
                                            setForceAwayScore(game.awayScore?.toString() || '');
                                            setShowForceStopModal(true);
                                        }}
                                    >
                                        <Square size={14} className="mr-1" />
                                        Force Stop
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <p>Game not found</p>
                )}
            </Modal>

            {/* Custom Push Modal */}
            <Modal
                isOpen={showCustomPushModal}
                onClose={() => setShowCustomPushModal(false)}
                title="Send Custom Notification"
                size="medium"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowCustomPushModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            onClick={handleSendCustomPush}
                            disabled={sendCustomPushMutation.isPending}
                        >
                            {sendCustomPushMutation.isPending ? 'Sending...' : 'Send Notification'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                        <div className="flex items-center gap-2 text-yellow-800 dark:text-yellow-200">
                            <AlertCircle size={16} />
                            <span className="text-sm">
                                {2 - (selectedGame?.customNotificationsCount || 0)} custom notification(s) remaining
                            </span>
                        </div>
                    </div>

                    <Input
                        label="Title"
                        value={customTitle}
                        onChange={(e) => setCustomTitle(e.target.value)}
                        placeholder="Notification title"
                    />

                    <Textarea
                        label="Body"
                        value={customBody}
                        onChange={(e) => setCustomBody(e.target.value)}
                        placeholder="Notification message"
                        rows={3}
                    />
                </div>
            </Modal>

            {/* Trigger Standard Push Modal */}
            <Modal
                isOpen={showTriggerModal}
                onClose={() => setShowTriggerModal(false)}
                title={`Trigger ${NOTIFICATION_LABELS[triggerType] || ''} Notification`}
                size="small"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowTriggerModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="warning"
                            onClick={handleTriggerPush}
                            disabled={triggerPushMutation.isPending}
                        >
                            {triggerPushMutation.isPending ? 'Sending...' : 'Confirm & Send'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                        <p className="text-sm text-orange-800 dark:text-orange-200 font-medium">
                            ⚠️ This will send a push notification to ALL users who have notifications enabled.
                        </p>
                    </div>

                    {['quarter_1_end', 'quarter_2_end', 'quarter_3_end', 'game_end', 'overtime_end', 'regulation_end'].includes(triggerType) && (
                        <>
                            <p className="text-sm text-muted-light dark:text-muted-dark">
                                Enter the scores to include in the notification:
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                <Input
                                    label={`${selectedGame?.homeTeam?.name || 'Home'} Score`}
                                    type="number"
                                    value={triggerHomeScore}
                                    onChange={(e) => setTriggerHomeScore(e.target.value)}
                                />
                                <Input
                                    label={`${selectedGame?.awayTeam?.name || 'Away'} Score`}
                                    type="number"
                                    value={triggerAwayScore}
                                    onChange={(e) => setTriggerAwayScore(e.target.value)}
                                />
                            </div>
                            {triggerType === 'game_end' && (
                                <div className="p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                                    <p className="text-sm text-yellow-800 dark:text-yellow-200">
                                        This will also update the final scores in the database.
                                    </p>
                                </div>
                            )}
                        </>
                    )}
                    {triggerType === 'game_start' && (
                        <p className="text-sm text-muted-light dark:text-muted-dark">
                            This will send a "Game Started" notification to all users.
                        </p>
                    )}
                </div>
            </Modal>

            {/* Force Stop Modal */}
            <Modal
                isOpen={showForceStopModal}
                onClose={() => setShowForceStopModal(false)}
                title="⚠️ Force Stop Game"
                size="small"
                footer={
                    <>
                        <Button variant="secondary" onClick={() => setShowForceStopModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="danger"
                            onClick={handleForceStop}
                            disabled={forceStopMutation.isPending}
                        >
                            {forceStopMutation.isPending ? 'Stopping...' : 'Confirm Force Stop'}
                        </Button>
                    </>
                }
            >
                <div className="space-y-4">
                    <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                        <p className="text-sm text-red-800 dark:text-red-200 font-medium mb-2">
                            🛑 Are you sure you want to force stop this game?
                        </p>
                        <ul className="text-sm text-red-700 dark:text-red-300 list-disc list-inside space-y-1">
                            <li>The live stream will be stopped immediately</li>
                            <li>The game will be marked as complete</li>
                            <li>This action cannot be undone</li>
                        </ul>
                    </div>

                    <p className="text-sm text-muted-light dark:text-muted-dark">
                        Set final scores (optional):
                    </p>

                    <div className="grid grid-cols-2 gap-4">
                        <Input
                            label={`${selectedGame?.homeTeam?.name || 'Home'} Score`}
                            type="number"
                            value={forceHomeScore}
                            onChange={(e) => setForceHomeScore(e.target.value)}
                        />
                        <Input
                            label={`${selectedGame?.awayTeam?.name || 'Away'} Score`}
                            type="number"
                            value={forceAwayScore}
                            onChange={(e) => setForceAwayScore(e.target.value)}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );
}

